import { z } from 'zod';

import { payPeriodTypeValues, type PayPeriodType } from './time-tracking-settings.dto.js';
import { WeekStartsOnSchema } from './time-tracking-settings.schema.js';
import {
  timeTrackingTimezoneSourceValues,
  type CreateTimeTrackingPolicyRequest,
  type CreateTimeTrackingPolicyResponse,
  type ListTimeTrackingPoliciesRequest,
  type ListTimeTrackingPoliciesResponse,
  type TimeTrackingPolicyDto,
  type TimeTrackingTimezoneSource,
  type UpdateTimeTrackingPolicyRequest,
  type UpdateTimeTrackingPolicyResponse,
} from './time-tracking-policy.dto.js';

const timestampSchema = z.iso.datetime();
const dateSchema = z.iso.date();

export const TimeTrackingPolicyPeriodTypeSchema: z.ZodType<PayPeriodType> = z.enum(payPeriodTypeValues);

export const TimeTrackingTimezoneSourceSchema: z.ZodType<TimeTrackingTimezoneSource> = z.enum(
  timeTrackingTimezoneSourceValues,
);

export const TimeTrackingPolicyDtoSchema: z.ZodType<TimeTrackingPolicyDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  effectiveFrom: dateSchema,
  effectiveTo: dateSchema.nullable(),
  periodType: TimeTrackingPolicyPeriodTypeSchema,
  weekStartsOn: WeekStartsOnSchema,
  timezoneSource: TimeTrackingTimezoneSourceSchema,
  graceWindowDaysForAdjustments: z.number().int().min(0).max(365),
  allowManagerAdjustPastPeriods: z.boolean(),
  allowEmployeeSelfAdjust: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const ListTimeTrackingPoliciesRequestSchema: z.ZodType<ListTimeTrackingPoliciesRequest> = z.object({
  companyId: z.uuid(),
});

export const ListTimeTrackingPoliciesResponseSchema: z.ZodType<ListTimeTrackingPoliciesResponse> = z.object({
  items: z.array(TimeTrackingPolicyDtoSchema),
});

export const CreateTimeTrackingPolicyRequestSchema: z.ZodType<CreateTimeTrackingPolicyRequest> = z.object({
  companyId: z.uuid(),
  effectiveFrom: dateSchema,
  effectiveTo: dateSchema.nullable().optional(),
  periodType: TimeTrackingPolicyPeriodTypeSchema,
  weekStartsOn: WeekStartsOnSchema,
  timezoneSource: TimeTrackingTimezoneSourceSchema,
  graceWindowDaysForAdjustments: z.number().int().min(0).max(365),
  allowManagerAdjustPastPeriods: z.boolean(),
  allowEmployeeSelfAdjust: z.boolean(),
});

export const CreateTimeTrackingPolicyResponseSchema: z.ZodType<CreateTimeTrackingPolicyResponse> = z.object({
  policy: TimeTrackingPolicyDtoSchema,
});

export const UpdateTimeTrackingPolicyRequestSchema: z.ZodType<UpdateTimeTrackingPolicyRequest> = z.object({
  companyId: z.uuid(),
  policyId: z.uuid(),
  effectiveFrom: dateSchema.optional(),
  effectiveTo: dateSchema.nullable().optional(),
  periodType: TimeTrackingPolicyPeriodTypeSchema.optional(),
  weekStartsOn: WeekStartsOnSchema.optional(),
  timezoneSource: TimeTrackingTimezoneSourceSchema.optional(),
  graceWindowDaysForAdjustments: z.number().int().min(0).max(365).optional(),
  allowManagerAdjustPastPeriods: z.boolean().optional(),
  allowEmployeeSelfAdjust: z.boolean().optional(),
});

export const UpdateTimeTrackingPolicyResponseSchema: z.ZodType<UpdateTimeTrackingPolicyResponse> = z.object({
  policy: TimeTrackingPolicyDtoSchema,
});
